import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarCheck2, MapPin, Printer, Save } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const CODES = { P: "text-emerald-500", A: "text-destructive", H: "text-yellow-500", L: "text-primary", O: "text-muted-foreground" };
const NEXT = { "": "P", P: "A", A: "H", H: "L", L: "O", O: "" };
const now = new Date();

export default function Attendance() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [dept, setDept] = useState("all");
  const [emps, setEmps] = useState([]);
  const [depts, setDepts] = useState([]);
  const [data, setData] = useState({}); // { employee_id: { "1": "P", ... } }
  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [month, year]);

  const load = async () => {
    try {
      const [e, d, a] = await Promise.all([api.get("/employees"), api.get("/departments"), api.get(`/attendance?month=${month}&year=${year}`)]);
      setEmps(e.data.filter(x => x.status !== "Inactive")); setDepts(d.data);
      const m = {}; a.data.forEach(r => { m[r.employee_id] = r.days || {}; }); setData(m);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [month, year]); // eslint-disable-line

  const set = (eid, day, val) => setData(d => ({ ...d, [eid]: { ...(d[eid] || {}), [day]: val } }));
  const cycle = (eid, day) => set(eid, day, NEXT[data[eid]?.[day] || ""]);

  const save = async () => {
    try {
      await Promise.all(emps.filter(e => dept === "all" || e.department === dept).map(e => api.post("/attendance", { month, year, employee_id: e.id, days: data[e.id] || {} })));
      toast.success("Attendance saved");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const filtered = emps.filter(e => dept === "all" || e.department === dept);

  const pg = usePagination(filtered);
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><CalendarCheck2 className="w-3 h-3"/> HRMS</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Attendance</h1>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="att-month" className="mt-1 rounded-sm bg-secondary border-border w-32"><SelectValue/></SelectTrigger>
              <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map(m => <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1).toLocaleString("en", { month: "long" })}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="att-year" className="mt-1 rounded-sm bg-secondary border-border w-24"><SelectValue/></SelectTrigger>
              <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Department</Label>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger data-testid="att-dept" className="mt-1 rounded-sm bg-secondary border-border w-40"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All Departments</SelectItem>{depts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="att-save" onClick={save} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider"><Save className="w-3 h-3 mr-1"/>Save</Button>
          <Link to="/hr/geo-attendance"><Button data-testid="att-geo-link" variant="outline" className="rounded-sm text-xs uppercase tracking-wider"><MapPin className="w-3 h-3 mr-1"/>Mark Attendance (GPS)</Button></Link>
          <Button variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/>Print</Button>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mb-2">Click a cell to cycle: P (Present) → A (Absent) → H (Half Day) → L (Leave) → O (Holiday) → blank.</div>
      <div className="border border-border bg-card rounded-sm overflow-auto">
        <table className="text-xs" data-testid="attendance-grid">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.15em] text-muted-foreground sticky top-0 z-10">
            <tr><th className="text-left p-2 sticky left-0 bg-secondary min-w-40">Employee</th>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => <th key={d} className="p-1 min-w-8 text-center">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={daysInMonth + 1} className="p-6 text-center text-muted-foreground">No employees for this filter</td></tr>}
            {pg.pageItems.map(e => (
              <tr key={e.id} data-testid={`att-row-${e.id}`} className="border-t border-border">
                <td className="p-2 sticky left-0 bg-card font-semibold whitespace-nowrap">{e.name}<span className="text-[10px] text-muted-foreground ml-1">({e.employee_code})</span></td>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
                  const v = data[e.id]?.[String(d)] || "";
                  return <td key={d} data-testid={`att-${e.id}-${d}`} onClick={() => cycle(e.id, String(d))} className={`p-1 text-center cursor-pointer font-bold hover:bg-secondary ${CODES[v] || ""}`}>{v || "·"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
